import { useForm, Controller } from 'react-hook-form';
import { Row, Col, Form, Card, ListGroup } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import './SlotCreate.css';
import { MdDelete } from 'react-icons/md';
import { IoMdAddCircleOutline } from 'react-icons/io';
import Toaster from '../../../../Components/Toaster/Toaster';
import { B2BAuthContext } from '../../../../Context/AuthContex/B2BAuthContext';
import { useB2BSlotsCreate } from '../../apiEndpoints/b2bBuyerSellerEndpoint';
import TimePicker from 'rc-time-picker';
import 'rc-time-picker/assets/index.css';
import moment from 'moment';
import CommonFormBtn from '../../../../Components/Common/CommonFormBtn';
import { useEffect } from 'react';

const Toast = Toaster();
type TimeSlot = {
  id: number;
  date: string;
  start_time: string;
  end_time: string;
  duration: string;
};

type FormData = {
  date: string;
  slots: {
    start_time: string;
    end_time: string;
  }[];
};

function SlotCreate({
  handleClose,
  type,
  user_id,
}: {
  handleClose?: () => void;
  type: 'B2B' | 'Exhibitor';
  user_id: number;
}) {
  const { mutate, isPending, isSuccess } = useB2BSlotsCreate();
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
    setValue,
  } = useForm<FormData>({
    defaultValues: {
      date: '',
      slots: [{ start_time: '', end_time: '' }],
    },
  });

  const onSubmit = (data: FormData) => {
    const updatedData = data?.slots?.map((item) => ({
      start_time: `${watch('date')}${item.start_time.substring(
        item.start_time.indexOf('T')
      )}`,
      end_time: `${watch('date')}${item.end_time.substring(
        item.end_time.indexOf('T')
      )}`,
    }));

    const isValidDataExist = validateAllSlots(updatedData as TimeSlot[]);
    if (isValidDataExist?.valid && user_id) {
      mutate({
        user_id: user_id,
        type: type,
        slots: updatedData,
      });
    }
  };

  useEffect(() => {
    if (isSuccess) {
      reset();
      handleClose?.();
    }
  }, [isSuccess]);

  const validate30MinuteSlot = (
    startDateTime: string,
    endDateTime: string
  ): boolean => {
    if (!startDateTime || !endDateTime) return false;
    // Create moment objects and ignore seconds
    const start = moment(startDateTime).startOf('minute');
    const end = moment(endDateTime).startOf('minute');

    // Calculate exact minute difference
    const diffMinutes = end.diff(start, 'minutes');
    return diffMinutes === 30;
  };

  const allSlots = watch('slots');
  return (
    <>
      <Row className=''>
        <Col md={10} lg={12}>
          <Card className='p-4 shadow'>
            <h3 className='text-center mb-4'>Create Time Slots</h3>
            <Form onSubmit={handleSubmit(onSubmit)}>
              <Form.Group className='mb-3'>
                <Form.Label>Date</Form.Label>
                <Controller
                  name='date'
                  control={control}
                  rules={{ required: 'Date is required' }}
                  render={({ field }) => (
                    <Form.Control
                      type='date'
                      {...field}
                      isInvalid={!!errors.date}
                    />
                  )}
                />
                {errors.date && (
                  <Form.Control.Feedback type='invalid'>
                    {errors.date.message}
                  </Form.Control.Feedback>
                )}
              </Form.Group>

              <Controller
                name='slots'
                control={control}
                rules={{
                  validate: (slots) => {
                    if (!slots || slots.length === 0) {
                      return 'At least one time slot is required';
                    }
                    return true;
                  },
                }}
                render={({ field }) => {
                  return (
                    <>
                      <ListGroup className='mb-3'>
                        {field.value.map((_, index) => (
                          <ListGroup.Item key={index} className='slot-item'>
                            <Row className='align-items-center'>
                              <Col md={5} className='d-flex flex-column'>
                                <Form.Label>Start Time</Form.Label>
                                <Controller
                                  name={`slots.${index}.start_time`}
                                  control={control}
                                  rules={{
                                    required: 'Start time is required',
                                  }}
                                  render={({ field }) => {
                                    return (
                                      <TimePicker
                                        {...field}
                                        value={
                                          field.value
                                            ? moment(field.value)
                                            : moment()
                                        } // Ensure proper moment format
                                        onChange={(value) => {
                                          console.log(value);
                                          field.onChange(
                                            value ? value.format() : ''
                                          );
                                        }}
                                        showSecond={false}
                                        use12Hours
                                        className={
                                          watch(`slots.${index}.start_time`)
                                            ? `timeColor`
                                            : ''
                                        }
                                      />
                                    );
                                  }}
                                />
                                <div>
                                  {errors.slots?.[index]?.start_time && (
                                    <Form.Text className='text-danger'>
                                      {errors.slots[index]?.start_time?.message}
                                    </Form.Text>
                                  )}
                                </div>
                              </Col>
                              <Col md={5} className='d-flex flex-column'>
                                <Form.Label>End Time</Form.Label>
                                <Controller
                                  name={`slots.${index}.end_time`}
                                  control={control}
                                  rules={{
                                    required: 'End time is required',

                                    validate: (endTime, { date }) => {
                                      const startISO = watch(
                                        `slots.${index}.start_time`
                                      );
                                      const endISO = watch(
                                        `slots.${index}.end_time`
                                      );
                                      return (
                                        validate30MinuteSlot(
                                          startISO,
                                          endISO
                                        ) ||
                                        'Time slot must be exactly 30 minutes'
                                      );
                                    },
                                  }}
                                  render={({ field }) => {
                                    return (
                                      <TimePicker
                                        {...field}
                                        value={
                                          field.value
                                            ? moment(field.value)
                                            : moment()
                                        } // Ensure proper moment format
                                        onChange={(value) =>
                                          field.onChange(
                                            value ? value.format() : ''
                                          )
                                        } // Pass formatted value to react-hook-form
                                        showSecond={false}
                                        use12Hours
                                        className={
                                          watch(`slots.${index}.end_time`)
                                            ? `timeColor`
                                            : ''
                                        }
                                      />
                                    );
                                  }}
                                />
                                <div>
                                  {errors.slots?.[index]?.end_time && (
                                    <Form.Text className='text-danger'>
                                      {errors.slots[index]?.end_time?.message}
                                    </Form.Text>
                                  )}
                                </div>
                              </Col>
                              <Col
                                md={2}
                                className='d-flex'
                                style={{ height: '70px' }} // Set a fixed height or ensure the parent defines the height
                              >
                                <>
                                  <span
                                    onClick={() => {
                                      const lastIndex = allSlots?.length - 1;
                                      const lastSlotEndTime =
                                        allSlots[lastIndex]?.end_time;

                                      const newDateTime = moment
                                        .parseZone(lastSlotEndTime)
                                        .format();

                                      field.onChange([
                                        ...allSlots,
                                        { start_time: '', end_time: '' },
                                      ]);

                                      if (lastSlotEndTime) {
                                        setValue(
                                          `slots.${index + 1}.start_time`,
                                          newDateTime
                                        );
                                      }
                                    }}
                                    className='mt-auto pointer' // Pushes the span to the bottom
                                  >
                                    <IoMdAddCircleOutline
                                      size={30}
                                      color='green'
                                    />
                                  </span>

                                  {index > 0 && (
                                    <span
                                      onClick={() => {
                                        const newSlots = [...allSlots];
                                        newSlots.splice(index, 1);
                                        field.onChange(newSlots);
                                      }}
                                      className='mt-auto pointer' // Pushes the span to the bottom
                                    >
                                      <MdDelete size={30} color='red' />
                                    </span>
                                  )}
                                </>
                              </Col>
                            </Row>
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    </>
                  );
                }}
              />
              <div className='d-grid'>
                <CommonFormBtn
                  loading={isPending}
                  btnText='Create Time Slots'
                />
              </div>
            </Form>
          </Card>
        </Col>
      </Row>
    </>
  );
}

export default SlotCreate;

export function validateAllSlots(slots: TimeSlot[]): {
  valid: boolean;
  conflicts: Array<{ slot1: TimeSlot; slot2: TimeSlot }>;
} {
  const conflicts: Array<{ slot1: TimeSlot; slot2: TimeSlot }> = [];

  // Helper to parse ISO datetime with timezone
  const parseDateTime = (datetime: string): Date => {
    return new Date(datetime);
  };

  // First check each individual slot is valid
  for (const slot of slots) {
    const start = parseDateTime(slot.start_time);
    const end = parseDateTime(slot.end_time);
    if (start >= end) {
      Toast.fire({
        icon: 'error',
        title: `Invalid slot: End time must be after start time`,
      });
      return {
        valid: false,
        conflicts: [{ slot1: slot, slot2: slot }],
      };
    }
  }

  // Then check for overlaps between different slots on the same date
  for (let i = 0; i < slots.length; i++) {
    for (let j = i + 1; j < slots.length; j++) {
      const slot1 = slots[i];
      const slot2 = slots[j];

      // Extract just the date part (without time) for comparison
      const date1 = slot1.start_time.split('T')[0];
      const date2 = slot2.start_time.split('T')[0];

      // Only compare slots on the same date
      if (date1 === date2) {
        const start1 = parseDateTime(slot1.start_time);
        const end1 = parseDateTime(slot1.end_time);
        const start2 = parseDateTime(slot2.start_time);
        const end2 = parseDateTime(slot2.end_time);

        if (
          (start1 >= start2 && start1 < end2) || // Slot1 starts during Slot2
          (end1 > start2 && end1 <= end2) || // Slot1 ends during Slot2
          (start1 <= start2 && end1 >= end2) // Slot1 completely contains Slot2
        ) {
          Toast.fire({
            icon: 'error',
            title: `Time conflict detected !`,
          });
          conflicts.push({ slot1, slot2 });
        }
      }
    }
  }

  return {
    valid: conflicts.length === 0,
    conflicts,
  };
}
